import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  Dispatch
} from 'react'
import { PasswordMaxDateValidationStatus } from 'common/enum/password-max-date-validation-status'
import { SchedulingStatus } from 'common/enum/scheduling-status'
import { SurgeryType } from 'common/enum/surgery-type'
import {
  SurgicalCenter,
  SurgicalCenterSchedule,
  SurgicalSchedulingSectorModel
} from 'domain/entities/surgical-scheduling-sector-model'
import { AnesthesistModel } from 'domain/entities/surgical-scheduling-sector-model'

export type HandleDataProps = {
  // Form Data
  selected_duration: string
  selected_surgical_center: SurgicalCenter
  selected_date: string
  selected_anesthesist: AnesthesistModel

  // Status
  has_validation_error: boolean
  show_modal_validation_error: boolean
  show_modal_equipment: boolean
  toggle_refresh_calendar: boolean

  // Schedule
  schedules: any // ToDo: adjust type
  surgical_center_schedule: SurgicalCenterSchedule

  // Create Schedule Data
  selected_room_id: number
  selected_hour_start: string
  selected_hour_end: string
  selected_hour_end_with_preparation: string
  reason_scheduling_another_date: string
  observation_scheduling_another_date: string
}

export type StateDataProps = SurgicalSchedulingSectorModel & HandleDataProps

// Estado inicial
const initialState: StateDataProps = {
  surgical_order_id: 0,
  hospitalization_type: '',
  freeze: false,
  extra_blood: false,
  cti: false,
  patient_hospitalized: false,
  surgical_center: '',
  surgery_type: {} as SurgeryType,
  technique: '',
  expired_password: {} as PasswordMaxDateValidationStatus,
  is_erp_integration: false,
  oncology_or_research: false,
  scheduling_status: {} as SchedulingStatus,
  surgery_name: '',
  surgery_date: '',
  solicitation_date: '',
  doctor_id: 0,
  doctor_name: '',
  doctor_specialty: '',
  patient_id: 0,
  patient_name: '',
  patient_birth_date: '',
  health_insurance_id: 0,
  health_insurance_name: '',
  average_time: '',
  requested_by_name: '',
  scheduled_by_name: '',
  created_at: '',
  hospital_id: 0,
  hospital_friendly_name: '',
  other_surgeries: [],
  procedures: [],
  equipment: [],
  appointments: [],

  // Form Data
  selected_duration: '',
  selected_surgical_center: {
    id: '',
    name: ''
  },
  selected_date: '',
  selected_anesthesist: {} as AnesthesistModel,

  // Status
  has_validation_error: false,
  show_modal_validation_error: false,
  show_modal_equipment: false,
  toggle_refresh_calendar: false,

  // Schedules
  surgical_center_schedule: {} as SurgicalCenterSchedule,
  schedules: '', // ToDo: adjust type

  // Create Schedule Data
  selected_room_id: 0,
  selected_hour_start: '',
  selected_hour_end: '',
  selected_hour_end_with_preparation: '',
  reason_scheduling_another_date: '',
  observation_scheduling_another_date: ''
}

// Define as ações para atualizar o estado
type CreateSurgicalSchedulingAction =
  | {
      type: 'SET_SURGICAL_SCHEDULING'
      payload: Partial<StateDataProps>
    }
  | { type: 'RESET_SURGICAL_SCHEDULING' }

// Reducer para gerenciar ações
const createSurgicalSchedulingReducer = (
  state: StateDataProps,
  action: CreateSurgicalSchedulingAction
): StateDataProps => {
  switch (action.type) {
    case 'SET_SURGICAL_SCHEDULING':
      return { ...state, ...action.payload }
    case 'RESET_SURGICAL_SCHEDULING':
      return initialState
    default:
      return state
  }
}

// Context
const CreateSurgicalSchedulingContext = createContext<{
  state: StateDataProps
  dispatch: Dispatch<CreateSurgicalSchedulingAction>
}>({
  state: initialState,
  dispatch: () => undefined
})

// Provider
export const CreateSurgicalSchedulingProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const [state, dispatch] = useReducer(
    createSurgicalSchedulingReducer,
    initialState
  )

  return (
    <CreateSurgicalSchedulingContext.Provider value={{ state, dispatch }}>
      {children}
    </CreateSurgicalSchedulingContext.Provider>
  )
}

// Hook do context
export const useCreateSurgicalSchedulingContext = () =>
  useContext(CreateSurgicalSchedulingContext)
